import { ArrowUpRight } from "lucide-react";
import { WA_DEFAULT, BRAND } from "../../lib/site";
import { SectionHeading } from "./SectionHeading";
import { Reveal, MaskedLine } from "./Reveal";

const chapters = [
  {
    num: "01",
    title: "Results-obsessed, not just design-obsessed",
    desc: "A beautiful website that never rings your phone is an expense, not an asset. Every layout we ship is engineered around one question: will this bring the client more business?",
  },
  {
    num: "02",
    title: "Proof before payment",
    desc: "Agencies ask for advances on faith. We build your demo first — with your real content — so you judge our work, not our promises.",
  },
  {
    num: "03",
    title: "Direct access, always",
    desc: "No account managers, no telephone games. You talk directly to the specialists designing and coding your site.",
  },
];

export const Founder = () => (
  <section id="about" data-testid="founder-section" className="relative py-24 sm:py-32 scroll-mt-20 overflow-hidden" aria-label="About the founder">
    <div className="glow-orb w-[500px] h-[500px] top-20 -left-52 opacity-60" />
    <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-12 gap-14 items-start">
      <div className="lg:col-span-5 relative">
        <Reveal>
          <div className="relative group" data-testid="founder-photo-frame">
            <div className="absolute -inset-3 border border-brand/25 translate-x-3 translate-y-3 transition-transform duration-700 group-hover:translate-x-1.5 group-hover:translate-y-1.5" aria-hidden="true" />
            <img
              src="/assets/founder.jpg"
              alt="Ritesh Maurya — Founder and Lead Developer at STUZIO. EDITFLOW, a web design agency in Varanasi, India"
              loading="lazy"
              className="relative w-full aspect-[4/5] object-cover img-dual"
            />
            <div className="absolute bottom-5 left-5 right-5 bg-ink/85 backdrop-blur-md border hairline p-4">
              <p className="font-display italic text-lg text-white">&ldquo;We don&apos;t sell websites. We sell what websites bring.&rdquo;</p>
              <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-brand mt-2">{BRAND.founder} · Founder</p>
            </div>
          </div>
        </Reveal>
      </div>

      <div className="lg:col-span-7">
        <SectionHeading
          eyebrow="Meet the Founder"
          lines={[<>The founder behind</>, <><em className="italic text-brand">Stuzio.</em></>]}
        />
        <Reveal delay={0.2}>
          <p className="mt-7 text-mute text-base sm:text-lg leading-relaxed max-w-2xl" data-testid="founder-bio">
            Founded by {BRAND.founder} in Varanasi, {BRAND.name} builds websites for clients
            across India and worldwide. Our rule is simple: prove the value first — then ask
            for payment.
          </p>
        </Reveal>

        <div className="mt-10 space-y-0 border-t hairline">
          {chapters.map((c, i) => (
            <Reveal key={c.num} delay={0.08 * i}>
              <div className="grid sm:grid-cols-[80px_1fr] gap-3 sm:gap-6 py-7 border-b hairline" data-testid={`founder-chapter-${c.num}`}>
                <span className="font-mono text-brand text-sm tracking-[0.2em] pt-1">{c.num}</span>
                <div>
                  <h3 className="font-display text-xl sm:text-2xl text-white font-medium">{c.title}</h3>
                  <p className="mt-2 text-mute text-sm sm:text-base leading-relaxed">{c.desc}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal delay={0.2}>
          <a
            data-testid="founder-chat-button"
            href={WA_DEFAULT}
            target="_blank"
            rel="noopener noreferrer"
            className="group mt-9 inline-flex items-center gap-3 pl-6 pr-1.5 py-1.5 border border-brand/60 text-white text-sm font-semibold hover:bg-brand/5 transition-colors duration-300"
          >
            Chat directly with our team
            <span className="btn-arrow-box"><ArrowUpRight size={15} /></span>
          </a>
        </Reveal>
      </div>
    </div>
  </section>
);
